import {LoadingOutlined} from "@ant-design/icons";
import Collapse from "@kunukn/react-collapse";
import {Breadcrumb, Button, Checkbox, Descriptions, Form, Input, InputNumber, message, Modal, Select, Space, Spin} from "antd";
import {useForm} from "antd/es/form/Form";
import React, {useCallback, useContext, useEffect, useState} from "react";
import {Link, useNavigate, useParams} from "react-router-dom";
import {AiTierServiceContext, AppContextContext, OrganizationServiceContext, OrganizationTagServiceContext} from "../../Contexts";
import Organization from "../../domain/Organization";
import {ServerConstraintViolationsHolder} from "../../sal-ui/ServerConstraintViolations";
import {DocumentTitle} from "../DocumentTitle";
import "./OrganizationDetail.module.css";
import * as globalStyles from "../App.module.css";
import {Feature, formatFeature} from "../../domain/Feature";
import QueryOptions from "../../sal-ui/QueryOptions";
import AiTier from "../../domain/AiTier";
import AiUsageOverview, {AiCreditUsage} from "../aiusage/AiUsageOverview";
import CreditsValue from "../common/CreditsValue";
import {formatCreditsCompact} from "../../utils/FormatUtils";
import AiCreditUsageIndicator from "../common/AiCreditUsageIndicator";
import HashColorTag from "../common/HashColorTag";
import SelectAllMultiSelect from "../common/SelectAllMultiSelect";

const serverViolationsHolder = new ServerConstraintViolationsHolder();

function OrganizationDetail() {
    const appContext = useContext(AppContextContext);
    const organizationService = useContext(OrganizationServiceContext);
    const organizationTagService = useContext(OrganizationTagServiceContext);
    const aiTierService = useContext(AiTierServiceContext);
    const navigate = useNavigate();
    const {organizationId}: any = useParams();
    const [editMode, setEditMode] = useState(false);
    const [organization, setOrganization] = useState<Organization>();
    const [aiTiers, setAiTiers] = useState<AiTier[]>([]);
    const [tags, setTags] = useState<string[]>([]);
    const [monthlyCreditUsage, setMonthlyCreditUsage] = useState<AiCreditUsage>();
    const [editForm] = useForm();
    const selectedFeatures: Feature[] = Form.useWatch("features", editForm) || [];
    const selectedAiTierId: string | undefined = Form.useWatch("aiTierId", editForm);
    const allowAiTierLimitExceeded: boolean = Form.useWatch("allowAiTierLimitExceeded", editForm) || false;
    const selectedAiTier = aiTiers.find(tier => tier.id === selectedAiTierId);
    const onMonthlyCreditUsageChange = useCallback((usage?: AiCreditUsage) => {
        setMonthlyCreditUsage(usage);
    }, []);

    const layout = {
        labelCol: {span: 6},
        wrapperCol: {span: 18},
    };

    const tailLayout = {
        wrapperCol: {offset: 6, span: 18},
    };

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        reload();

        aiTierService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
            .then(result => setAiTiers(result.data));

        organizationTagService.getList()
            .then(setTags);
    }, [])

    return (
        <DocumentTitle title={organization?.name}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}>
                    <Breadcrumb.Item>{appContext.config?.appName}</Breadcrumb.Item>
                    <Breadcrumb.Item><Link to={"/organizations"}>Organizations</Link></Breadcrumb.Item>
                    <Breadcrumb.Item>{organization?.name}</Breadcrumb.Item>
                </Breadcrumb>

                <Spin spinning={!organization} indicator={<LoadingOutlined style={{fontSize: 24}} spin={true}/>}>
                    <h1>{organization?.name}</h1>

                    <div className={globalStyles["common__top-button-bar"]}>
                        <Button onClick={() => setEditMode(!editMode)}>Edit</Button>

                        <Button danger={true}
                                title={"Delete"}
                                onClick={() => {
                                    Modal.confirm({
                                        content: "Do you really want to delete this organization?",
                                        okText: "Delete",
                                        cancelText: "Cancel",
                                        okButtonProps: {danger: true},
                                        onOk: () => onDeleteConfirm(organization!)
                                    });
                                }}>Delete</Button>
                    </div>

                    {renderEditForm()}

                    <Descriptions column={1} bordered={true} size="small" className={globalStyles.details}>
                        <Descriptions.Item label={"Name"}>{organization?.name}</Descriptions.Item>
                        <Descriptions.Item label={"Organization ID"}>{organization?.organizationId}</Descriptions.Item>
                        <Descriptions.Item label={"Features"}>
                            {organization?.features.map(feature => <div key={feature}>{formatFeature(feature)}</div>)}
                        </Descriptions.Item>
                        {organization?.features.includes(Feature.AI) && (
                            <Descriptions.Item label={"AI tier"}>
                                {organization.aiTier ? (
                                    <>
                                        <Link to={`/definitions/ai-tiers/${organization.aiTier.id}`}>{organization.aiTier.name}</Link>
                                        <span> (</span>
                                        <CreditsValue value={organization.aiTier.maxCreditsPerMonth} compact={true} showIcon={false}/>
                                        <span>)</span>
                                    </>
                                ) : <i>not set</i>}
                            </Descriptions.Item>
                        )}
                        {organization?.features.includes(Feature.AI) && (
                            <Descriptions.Item label={"AI tier limit overage"}>
                                {organization.allowAiTierLimitExceeded ? "allowed" : "not allowed"}
                            </Descriptions.Item>
                        )}
                        {organization?.features.includes(Feature.AI) && organization.allowAiTierLimitExceeded && (
                            <Descriptions.Item label={"Monthly hard limit"}>
                                <CreditsValue value={organization.aiHardCreditLimitPerMonth} compact={true}/>
                            </Descriptions.Item>
                        )}
                        <Descriptions.Item label={"Tags"}>
                            <Space size={[4, 4]} wrap>
                                {organization?.tags?.map(tag => <HashColorTag key={`${organization.id}-${tag}`} value={tag}/>)}
                            </Space>
                        </Descriptions.Item>
                    </Descriptions>

                    {appContext.user.isSystemAdmin() && organization?.id && organization.features.includes(Feature.AI) && (
                        <>
                            <div style={{marginBottom: 16}}>
                                <h2 style={{display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: monthlyCreditUsage ? 4 : undefined}}>
                                    <span>AI usage</span>
                                </h2>
                                {monthlyCreditUsage && (
                                    <AiCreditUsageIndicator
                                        label="Used this month"
                                        used={monthlyCreditUsage.used}
                                        limit={monthlyCreditUsage.limit}
                                    />
                                )}
                            </div>
                            <AiUsageOverview
                                organizationId={organization.id}
                                aiTier={organization.aiTier}
                                onMonthlyCreditUsageChange={onMonthlyCreditUsageChange}
                            />
                        </>
                    )}

                </Spin>
            </>
        </DocumentTitle>
    )

    function renderEditForm() {
        return (
            <Collapse isOpen={editMode}>
                <h3>Edit of organization {organization?.name}</h3>

                <Form {...layout} form={editForm} className={`${globalStyles["common__form"]} ${globalStyles["common_form--edit"]}`} onFinish={onFinishEdit}>
                    <Form.Item
                        name={"name"}
                        label={"Name"}
                        rules={[
                            {required: true, message: "Name is required."},
                            {validator: serverViolationsHolder.createServerValidator('UNIQUE', 'name', undefined, {compareLowerCaseValues: true}), message: "Name is already used."}
                        ]}>
                        <Input maxLength={100}/>
                    </Form.Item>

                    <Form.Item
                        name={"organizationId"}
                        label={"Organization ID"}
                        rules={[
                            {required: true, message: "Organization ID is required."},
                            {validator: serverViolationsHolder.createServerValidator('UNIQUE', 'organizationId', undefined, {compareLowerCaseValues: true}), message: "Organization ID is already used."}
                        ]}>
                        <Input maxLength={50}/>
                    </Form.Item>

                    <Form.Item
                        name={["features"]}
                        label={"Features"}
                        rules={[{required: true, message: "Please select at least one feature."}]}>
                        <SelectAllMultiSelect
                            selectAllValues={Object.values(Feature)}
                            options={Object.values(Feature).map(feature => ({
                                label: formatFeature(feature),
                                value: feature
                            }))}
                        />
                    </Form.Item>

                    <Form.Item name={["tags"]} label={"Tags"}>
                        <Select mode="tags" options={tags.map(tag => ({value: tag, label: tag}))}/>
                    </Form.Item>

                    {selectedFeatures.includes(Feature.AI) && (
                        <>
                            <Form.Item
                                name={"aiTierId"}
                                label={"AI tier"}
                                rules={[{required: true, message: "AI tier is required when AI feature is enabled."}]}>
                                <Select
                                    allowClear
                                    options={aiTiers.map(tier => ({
                                        label: formatAiTierOption(tier),
                                        value: tier.id
                                    }))}
                                />
                            </Form.Item>

                            <Form.Item
                                name={"allowAiTierLimitExceeded"}
                                label={"AI credits"}
                                valuePropName={"checked"}>
                                <Checkbox>Allow usage above tier limit</Checkbox>
                            </Form.Item>

                            {allowAiTierLimitExceeded && (
                                <Form.Item
                                    name={"aiHardCreditLimitPerMonth"}
                                    label={"Monthly hard limit"}
                                    rules={[
                                        {required: true, message: "Monthly hard limit is required when usage above tier limit is allowed."},
                                        {validator: validateHardCreditLimit}
                                    ]}>
                                    <InputNumber min={0} precision={2}/>
                                </Form.Item>
                            )}
                        </>
                    )}

                    <Form.Item {...tailLayout} className={globalStyles["common__form-buttons"]}>
                        <Button type={"primary"} htmlType={"submit"}>{"Save"}</Button>
                        <Button onClick={() => setEditMode(false)}>{"Cancel"}</Button>
                    </Form.Item>
                </Form>
            </Collapse>
        )
    }

    function onFinishEdit(values: any) {
        if (!values.features?.includes(Feature.AI)) {
            values.aiTierId = undefined;
            values.allowAiTierLimitExceeded = false;
            values.aiHardCreditLimitPerMonth = undefined;
        } else if (!values.allowAiTierLimitExceeded) {
            values.aiHardCreditLimitPerMonth = undefined;
        }

        organizationService.update(organizationId!, values)
            .then(
                () => {
                    message.success(<>Organization <b>{values.name}</b> successfully updated.</>);

                    setEditMode(false);

                    reload();
                },
                error => serverViolationsHolder.handleServerError(error, editForm)
            );
    }

    function onDeleteConfirm(organization: Organization) {
        organizationService.delete(organization)
            .then(() => {
                message.success(<>Organization <b>{organization.name}</b> successfully deleted.</>);

                navigate(`/organizations`)
            });
    }

    function reload() {
        organizationService.get(organizationId!).then(organization => {
            setOrganization(organization);

            editForm.setFieldsValue({
                ...organization,
                aiTierId: organization.aiTier?.id
            });
        });
    }

    function validateHardCreditLimit(_: unknown, value?: number) {
        const tierLimit = selectedAiTier?.maxCreditsPerMonth;

        if (!allowAiTierLimitExceeded || tierLimit == null || value == null) {
            return Promise.resolve();
        }

        if (value < tierLimit) {
            return Promise.reject(new Error("Monthly hard limit must be greater than or equal to AI tier limit."));
        }

        return Promise.resolve();
    }

    function formatAiTierOption(tier: AiTier) {
        return `${tier.name} (${formatCreditsCompact(tier.maxCreditsPerMonth)})`;
    }

}

export default OrganizationDetail;
